<template>
  <div class="work-page">

    <!-- ── Page Header ── -->
    <section class="page-header">
      <h1 class="page-title">UX Case Studies</h1>
      <p class="page-subtitle">
        A collection of UX design projects spanning enterprise SaaS, healthcare, IoT, and
        emerging technologies—representing 15+ years of industry experience.
      </p>
    </section>

    <!-- ── Case Studies Grid ── -->
    <section class="cases-section">
      <div class="cases-grid">
        <div
          v-for="(study, idx) in uxCaseStudies"
          :key="idx"
          class="case-card"
        >
          <div class="case-card-img-wrap">
            <img :src="study.img" :alt="study.title" class="case-card-img" />
          </div>
          <div class="case-card-body">
            <div class="case-card-tags">
              <span
                v-for="(tag, ti) in study.tag.split(', ')"
                :key="ti"
                class="case-tag"
              >{{ tag }}</span>
            </div>
            <h3 class="case-card-title">{{ study.title }}</h3>
            <p class="case-card-desc">{{ study.description }}</p>
            <a
              href="#"
              class="case-view-link"
              @click.prevent="openPasswordModal(study.title, study.content, study.isImage, study.isPdf)"
            >View Case Study &rarr;</a>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Bottom Sheet Modal ── -->
    <BottomSheetModal
      v-if="isMentorshipModalVisible"
      :isVisible="isMentorshipModalVisible"
      :title="mentorshipModalTitle"
      :content="mentorshipModalContent"
      :isImage="mentorshipIsImage"
      :isPdf="mentorshipIsPdf"
      @close="closeMentorshipModal"
    />

    <!-- ── Password Modal ── -->
    <transition name="fade">
      <div v-if="isPasswordModalVisible" class="modal-overlay">
        <div class="password-modal">
          <h2 class="modal-heading">Password Required</h2>
          <p class="modal-sub">Please enter the password to access this case study.</p>
          <div class="input-wrap">
            <input
              type="password"
              v-model="inputPassword"
              placeholder="Enter Password"
              class="password-input"
            />
          </div>
          <div class="modal-actions">
            <button class="btn-cancel" @click="closePasswordModal">Close</button>
            <button class="btn-submit" @click="checkPassword">Submit</button>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import BottomSheetModal from "@/components/BottomSheetModal.vue";

const imageMap = require.context('@/assets', false, /\.(png|jpe?g|gif|svg|webp)$/);
function assetUrl(filename) {
  try { return imageMap('./' + filename); } catch { return ''; }
}

export default {
  name: "WorkPage",
  components: { BottomSheetModal },
  data() {
    return {
      isPasswordModalVisible: false,
      inputPassword: "",
      modalTitle: "",
      modalContent: "",
      isContentImage: false,
      isContentPdf: false,
      isMentorshipModalVisible: false,
      mentorshipModalTitle: "",
      mentorshipModalContent: "",
      mentorshipIsImage: false,
      mentorshipIsPdf: false,
      uxCaseStudies: [],
    };
  },
  methods: {
    openPasswordModal(title, content, isImage, isPdf) {
      this.modalTitle = title;
      this.modalContent = content;
      this.isContentImage = isImage;
      this.isContentPdf = isPdf;
      this.isPasswordModalVisible = true;
    },
    closePasswordModal() {
      this.isPasswordModalVisible = false;
      this.inputPassword = "";
    },
    checkPassword() {
      if (this.inputPassword === "Rbwork@ux") {
        this.closePasswordModal();
        this.mentorshipModalTitle = this.modalTitle;
        this.mentorshipModalContent = this.modalContent;
        this.mentorshipIsImage = this.isContentImage;
        this.mentorshipIsPdf = this.isContentPdf;
        this.isMentorshipModalVisible = true;
      } else {
        alert("Incorrect password. Please try again.");
      }
    },
    closeMentorshipModal() {
      this.isMentorshipModalVisible = false;
    },
  },
  async mounted() {
    try {
      const url = process.env.BASE_URL + 'data/work.json';
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status} fetching ${url}`);
      const data = await res.json();
      this.uxCaseStudies = data.uxCaseStudies.map(s => ({ ...s, img: assetUrl(s.img) }));
    } catch (err) {
      console.error('work.json load failed:', err);
    }
  },
};
</script>

<style scoped>
/* ── Page wrapper ── */
.work-page {
  background: var(--color-bg-white);
  min-height: 100vh;
  overflow-x: hidden;
}

/* ── Page Header ── */
.page-header {
  text-align: center;
  padding: 7rem var(--space-6) var(--space-12);
  background: var(--color-bg-light);
  border-bottom: 1px solid var(--color-bg-subtle);
}
/* .page-title and .page-subtitle come from global style.css */

/* ── Grid Section ── */
.cases-section {
  max-width: var(--container-xl);
  margin: 0 auto;
  padding: var(--space-16) var(--space-6) var(--section-py);
}
.cases-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-8);
}

/* ── Case Card ── */
.case-card {
  background: var(--color-bg-white);
  border-radius: var(--radius-xl);
  border: 1px solid var(--color-border);
  box-shadow: var(--shadow-sm);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: box-shadow var(--duration-base) var(--ease-out), transform var(--duration-base) var(--ease-out);
}
.case-card:hover {
  box-shadow: var(--shadow-xl);
  transform: translateY(-4px);
}

.case-card-img-wrap {
  width: 100%;
  height: 220px;
  overflow: hidden;
  background: var(--color-bg-subtle);
  display: flex;
  align-items: center;
  justify-content: center;
}
.case-card-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  transition: transform var(--duration-slow) var(--ease-out);
}
.case-card:hover .case-card-img {
  transform: scale(1.04);
}

.case-card-body {
  padding: var(--space-6);
  display: flex;
  flex-direction: column;
  flex: 1;
}

/* Tags */
.case-card-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.85rem;
}
.case-tag {
  font-size: var(--text-xs);
  font-weight: var(--font-medium);
  color: var(--color-text-400);
  background: var(--color-bg-subtle);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-full);
  padding: 0.25rem 0.75rem;
  white-space: nowrap;
}

/* Title */
.case-card-title {
  font-size: var(--text-base);
  font-weight: var(--font-bold);
  color: var(--color-primary);
  line-height: var(--leading-snug);
  margin: 0 0 0.6rem;
}

/* Description */
.case-card-desc {
  font-size: var(--text-sm);
  color: var(--color-text-400);
  line-height: var(--leading-relaxed);
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: var(--space-5);
}

/* View link */
.case-view-link {
  font-size: var(--text-sm);
  font-weight: var(--font-semibold);
  color: var(--color-primary);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  transition: gap var(--duration-base) var(--ease-out), color var(--duration-base) var(--ease-out);
  margin-top: auto;
}
.case-view-link:hover {
  color: var(--color-primary-dark);
  gap: 0.5rem;
}

/* ── Password Modal ── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  z-index: 999;
  display: flex;
  justify-content: center;
  align-items: center;
}
.password-modal {
  background: var(--color-bg-white);
  width: 100%;
  max-width: 380px;
  border-radius: var(--radius-xl);
  padding: var(--space-8);
  box-shadow: var(--shadow-2xl);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  z-index: 1000;
}
.modal-heading {
  font-size: var(--text-2xl);
  font-weight: var(--font-bold);
  color: var(--color-bg-darker);
  margin: 0;
}
.modal-sub {
  font-size: var(--text-sm);
  color: var(--color-text-400);
  margin: 0;
}
.input-wrap {
  display: flex;
  justify-content: center;
}
.password-input {
  width: 100%;
  padding: 0.75rem var(--space-4);
  border: 1.5px solid var(--color-border-strong);
  border-radius: var(--radius-md);
  font-size: var(--text-base);
  outline: none;
  transition: border-color var(--duration-base) var(--ease-out);
}
.password-input:focus {
  border-color: var(--color-primary);
}
.modal-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
}
.btn-cancel {
  background: var(--color-bg-subtle);
  color: var(--color-text-700);
  border: none;
  border-radius: var(--radius-md);
  padding: 0.65rem var(--space-6);
  font-weight: var(--font-semibold);
  cursor: pointer;
  transition: background var(--duration-base) var(--ease-out);
}
.btn-cancel:hover { background: var(--color-border); }
.btn-submit {
  background: var(--color-primary);
  color: var(--color-bg-white);
  border: none;
  border-radius: var(--radius-md);
  padding: 0.65rem var(--space-6);
  font-weight: var(--font-semibold);
  cursor: pointer;
  transition: background var(--duration-base) var(--ease-out);
}
.btn-submit:hover { background: var(--color-primary-dark); }

/* Fade transition */
.fade-enter-active,
.fade-leave-active { transition: opacity var(--duration-base) var(--ease-out); }
.fade-enter-from,
.fade-leave-to { opacity: 0; }

/* ── Responsive ── */
@media (max-width: 1024px) {
  .cases-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 640px) {
  .cases-grid { grid-template-columns: 1fr; }
  .page-header { padding: 6rem var(--space-4) var(--space-10); }
  .cases-section { padding: var(--space-10) var(--space-4) var(--space-16); }
}
</style>
